import { apiService } from './api';
import {
  APIKey,
  CreateAPIKeyRequest,
  CreateAPIKeyResponse,
  UpdateAPIKeyRequest,
  APIKeyListResponse,
  APIKeyUsageStats,
  ApiResponse,
  APIKeyType,
} from '../types';

class APIKeyApi {
  async createAPIKey(request: CreateAPIKeyRequest): Promise<CreateAPIKeyResponse> {
    const response = await apiService.post<ApiResponse<CreateAPIKeyResponse>>(
      '/v1/admin/api-keys',
      request
    );
    if (!response.data) {
      throw new Error('Failed to create API key');
    }
    return response.data;
  }

  async listAPIKeys(type?: APIKeyType): Promise<APIKey[]> {
    const params = type ? `?type=${type}` : '';
    const response = await apiService.get<ApiResponse<APIKeyListResponse>>(
      `/v1/admin/api-keys${params}`
    );
    return response.data?.apiKeys || [];
  }

  async getAPIKey(id: string): Promise<APIKey> {
    const response = await apiService.get<ApiResponse<APIKey>>(
      `/v1/admin/api-keys/${id}`
    );
    if (!response.data) {
      throw new Error('API key not found');
    }
    return response.data;
  }

  async updateAPIKey(id: string, request: UpdateAPIKeyRequest): Promise<APIKey> {
    const response = await apiService.put<ApiResponse<APIKey>>(
      `/v1/admin/api-keys/${id}`,
      request
    );
    if (!response.data) {
      throw new Error('Failed to update API key');
    }
    return response.data;
  }

  async revokeAPIKey(id: string): Promise<void> {
    await apiService.post(`/v1/admin/api-keys/${id}/revoke`);
  }

  async activateAPIKey(id: string): Promise<APIKey> {
    return this.updateAPIKey(id, { status: 'active' });
  }

  async deleteAPIKey(id: string): Promise<void> {
    await apiService.delete(`/v1/admin/api-keys/${id}`);
  }

  async getAPIKeyUsageStats(id: string): Promise<APIKeyUsageStats> {
    const response = await apiService.get<ApiResponse<APIKeyUsageStats>>(
      `/v1/admin/api-keys/${id}/usage`
    );
    if (!response.data) {
      throw new Error('Failed to get usage stats');
    }
    return response.data;
  }

  // Helper method to get the masked key display
  getMaskedKey(apiKey: APIKey): string {
    return `${apiKey.keyPrefix}...${apiKey.keySuffix}`;
  }

  // Helper method to check if key is expired
  isExpired(apiKey: APIKey): boolean {
    if (!apiKey.expiresAt) return false;
    return new Date(apiKey.expiresAt) < new Date();
  }

  // Helper method to get status badge color
  getStatusColor(apiKey: APIKey): 'success' | 'warning' | 'failure' {
    if (apiKey.status === 'active' && !this.isExpired(apiKey)) {
      return 'success';
    } else if (apiKey.status === 'revoked') {
      return 'failure';
    } else {
      return 'warning';
    }
  }

  // Helper method to get type badge color
  getTypeColor(type: APIKeyType): 'indigo' | 'purple' | 'pink' {
    switch (type) {
      case 'iot':
        return 'indigo';
      case 'webhook':
        return 'purple';
      case 'admin':
        return 'pink';
      default:
        return 'indigo';
    }
  }
}

export const apiKeyApi = new APIKeyApi();